import { Component, ChangeDetectionStrategy, signal, output, input, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Constants } from '@/src/helpers/constants';
import { MasterService } from '@/src/services/master.service';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';

export type PaymentMethod = 'Card' | 'Cash' | 'EasyPay' | 'Points';
export type PaymentStep = 'selection' | 'card' | 'cash' | 'easypay' | 'points' | 'complete';

export interface PaymentMethodOption {
  id: number;
  titleKey: string;
  subtitleKey: string;
  icon: string;
  color: 'indigo' | 'emerald' | 'amber' | 'purple';
  enabled: boolean;
}

export interface PaymentResult {
  method: PaymentMethod;
  amount: number;
  receivedAmount?: number;
  installments?: string;
  selectedEasyPayment?: any
}

@Component({
  selector: 'app-payment-method-selector',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, InputTextModule, InputNumberModule, DialogModule],
  templateUrl: './payment-method-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodSelectorComponent {
  paymentType = Constants.paymentType;
  // Inputs
  amount = input<number>(0);
  title = input<string>('');
  enablePoints = input<boolean>(false);
  easyPayments = input<any[]>([]);
  paySuccess = input<boolean>(false)

  // Outputs
  paymentCompleted = output<PaymentResult>();
  cancelled = output<void>();
  payComplete = output<any>();

  // Internal state
  paymentStep = signal<PaymentStep>('selection');
  selectedPaymentMethod = signal<number>(Constants.paymentType.Cash);
  installmentCount = signal<string>('00');
  receivedAmount = signal<number>(0);
  countdown = signal(10);
  private countdownInterval: any;
  Change_AMT: number = 0;
  selectedEasyPayment: any;
  mon_cnt = '00';

  // Available payment methods
  paymentMethods = signal<PaymentMethodOption[]>([
    {
      id: this.paymentType.Card,
      titleKey: 'PAYMESTR0005',
      subtitleKey: 'PAYMESTR0006',
      icon: 'assets/icons/Card.svg',
      color: 'indigo',
      enabled: true
    },
    {
      id: this.paymentType.Cash,
      titleKey: 'PAYMESTR0007',
      subtitleKey: 'PAYMESTR0008',
      icon: 'assets/icons/Cash.svg',
      color: 'emerald',
      enabled: true
    },
    {
      id: this.paymentType.EasyPay,
      titleKey: 'PAYMESTR0009',
      subtitleKey: 'PAYMESTR0010',
      icon: 'assets/icons/Card.svg',
      color: 'amber',
      enabled: true
    }
  ]);

  constructor(private masterService: MasterService, private translate: TranslateService) {
    effect((onCleanup) => {
      if (this.paySuccess() === true) {
        this.countdown.set(10);
        this.countdownInterval = setInterval(() => {
          this.countdown.update(c => {
            if (c <= 1) {
              clearInterval(this.countdownInterval);
              this.paymentSuccess();
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      }
      onCleanup(() => {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
      });
    });
  }

  ngOnInit() {
    this.refershCash()
  }

  refershCash() {
    this.Change_AMT = 0;
  }

  selectMethod(method: number) {
    this.selectedPaymentMethod.set(method);
    switch (method) {
      case this.paymentType.Card:
        this.paymentStep.set('card');
        break;
      case this.paymentType.Cash:
        this.paymentStep.set('cash');
        this.receivedAmount.set(this.amount());
        break;
      case this.paymentType.EasyPay:
        this.paymentStep.set('easypay');
        this.visibleEasypayment = true;
        break;
      default:
        break;
    }
  }

  changereceivedAmount(data: any) {
    this.receivedAmount.set(data)
    this.Change_AMT = this.receivedAmount() - this.amount() > 0 ? this.receivedAmount() - this.amount() : 0;
  }

  backToSelection() {
    this.refershCash();
    this.paymentStep.set('selection');
    this.selectedPaymentMethod.set(0);
    this.installmentCount.set('00');
    this.receivedAmount.set(0);
  }

  processPayment(additionalData?: any) {
    if (this.selectedPaymentMethod() === this.paymentType.EasyPay && !this.selectedEasyPayment) {
      this.masterService.showError(this.translate.instant('PAYMESTR0057'))
      return;
    }
    if (this.selectedPaymentMethod() === this.paymentType.Cash && this.receivedAmount() < this.amount()) {
      this.masterService.showError(this.translate.instant('PAYMESTR0055'))
      return;
    }
    const method = this.selectedPaymentMethod();
    if (!method) return;

    const result: PaymentResult = {
      method: method,
      amount: this.amount(),
      ...additionalData,
      selectedEasyPayment: this.selectedEasyPayment
    };

    if (method === this.paymentType.Card) {
      result.installments = this.installmentCount();
    } else if (method === this.paymentType.Cash) {
      result.receivedAmount = this.receivedAmount();
    }

    this.paymentCompleted.emit(result);
  }

  cancel() {
    this.cancelled.emit();
  }

  isMethodEnabled(method: PaymentMethodOption): boolean {
    if (method.id === this.paymentType.Points) {
      return this.enablePoints();
    }
    return method.enabled;
  }

  getColorClasses(color: string, isHover: boolean = false): string {
    const baseClasses: Record<string, string> = {
      'indigo': isHover ? 'hover:border-indigo-500' : 'text-indigo-600',
      'emerald': isHover ? 'hover:border-emerald-500' : 'text-emerald-600',
      'amber': isHover ? 'hover:border-amber-400' : 'text-amber-500',
      'purple': isHover ? 'hover:border-purple-500' : 'text-purple-600'
    };
    return baseClasses[color] || '';
  }

  visibleEasypayment: boolean = false;
  selectedEasyPay(type: any) {
    this.visibleEasypayment = false;
    this.selectedEasyPayment = type;
  }

  paymentSuccess() {
    const method = this.selectedPaymentMethod();
    const result = {
      method: method,
      amount: this.amount(),
    };

    this.payComplete.emit(result);
  }

  onKeyDownMonCNT(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'
    ];

    if (
      !/[0-9]/.test(event.key) &&
      !allowedKeys.includes(event.key)
    ) {
      event.preventDefault();
    }
  }

  onInputMonCNT(event: Event) {
    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/[^0-9]/g, '');

    if (value === '') {
      this.installmentCount.set('');
      return;
    }

    value = value.slice(0, 2);

    const num = Number(value);

    if (num > 99) {
      value = '99';
    }

    // update lại nhưng GIỮ format
    input.value = value;
    this.installmentCount.set(value);
  }
}
